import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Input } from "../../../ui/input";
import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../../../ui/dropdown-menu";
import { Label } from "../../../ui/label";
import { FileTextIcon, TagIcon, PlusIcon, XIcon } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { usePermissions } from "../../../../PermissionsProvider";
import { useTagConfig } from "../../../../hooks/useTagConfig";

interface DatasetNameProps {
  name?: string;
  description?: string;
  tags?: string[];
  onChange: (name: string, description: string, tags?: string[]) => void;
}

const DatasetName: React.FC<DatasetNameProps> = ({
  name,
  description,
  tags = [],
  onChange,
}) => {
  const { apiKey } = usePermissions();
  const { validTags, getTagDisplayConfig } = useTagConfig(apiKey);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const handleToggleTag = (tag: string) => {
    const newTags = tags.includes(tag)
      ? tags.filter(t => t !== tag)
      : [...tags, tag];
    onChange(name || "", description || "", newTags);
  };

  const handleRemoveTag = (tag: string) => {
    const newTags = tags.filter(t => t !== tag);
    onChange(name || "", description || "", newTags);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Dataset Details
        </h2>
        <p className="text-muted-foreground font-gc-reg">
          Enter a name and description for your dataset
        </p>
      </div>

      {/* Form Card */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <FileTextIcon className="w-5 h-5 text-maitai-green" />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Label htmlFor="dataset-name" className="text-sm font-gc-med text-foreground">
                Name
              </Label>
              <span className="text-red-500 text-sm">*</span>
            </div>
            <Input
              id="dataset-name"
              value={name || ""}
              onChange={(e) => onChange(e.target.value, description || "", tags)}
              placeholder="Enter dataset name"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              A unique name for your dataset
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Label htmlFor="dataset-description" className="text-sm font-gc-med text-foreground">
                Description
              </Label>
              <span className="text-red-500 text-sm">*</span>
            </div>
            <textarea
              id="dataset-description"
              value={description || ""}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(name || "", e.target.value, tags)}
              placeholder="Enter dataset description"
              rows={4}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground">
              Provide a brief description of this dataset and its intended use
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tags Card */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
            <TagIcon className="w-5 h-5 text-maitai-green" />
            Tags
            <span className="text-xs font-gc-reg text-muted-foreground ml-2">(optional)</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Add tags to categorize your dataset. Tags help filter datasets when creating compositions.
          </p>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* Display current tags */}
            {tags.map(tag => {
              const config = getTagDisplayConfig(tag);
              return (
                <Badge
                  key={tag}
                  variant="outline"
                  className={cn(
                    "text-xs h-6 gap-1 pr-1",
                    config.className
                  )}
                >
                  {config.label}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5 transition-colors"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                </Badge>
              );
            })}

            {/* Add tag dropdown */}
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs gap-1"
                >
                  <PlusIcon className="w-3 h-3" />
                  Add Tag
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel className="flex items-center gap-2">
                  <TagIcon className="w-4 h-4" />
                  Dataset Tags
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {validTags.map(tag => {
                  const config = getTagDisplayConfig(tag);
                  const isSelected = tags.includes(tag);
                  return (
                    <DropdownMenuCheckboxItem
                      key={tag}
                      checked={isSelected}
                      onCheckedChange={() => handleToggleTag(tag)}
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className={cn("font-medium", isSelected && "text-foreground")}>
                          {config.label}
                        </span>
                        {config.description && (
                          <span className="text-xs text-muted-foreground">
                            {config.description}
                          </span>
                        )}
                      </div>
                    </DropdownMenuCheckboxItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Empty state */}
            {tags.length === 0 && (
              <span className="text-sm text-muted-foreground ml-2">No tags selected</span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DatasetName; 